import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import _omit from 'lodash/omit';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Form from '@lib/components/ReactHookForm/FormContainer';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { Values } from '@lib/interfaces/form';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  AiMediaKindEnum,
  DocumentationVersionCreateInputObject,
  useEdmDocumentationNameQuery,
  useEdmDocumentationVersionCreateMutation,
} from 'graphql-common';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import { APP_URLS } from 'constants/urls';
import useAiBudgetStatus from 'components/AiAssistant/hooks/useAiBudgetStatus';
import useAiIndexableMediaKinds from 'components/AiAssistant/hooks/useAiIndexableMediaKinds';
import DocumentVersionFormActions from './components/DocumentVersionFormActions';
import getVersionFormFields, {
  VersionFormFieldNames,
} from './utils/getVersionFormFields';

export default function DocumentVersionForm() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { id = '' } = useParams();

  const {
    data: queryData,
    previousData,
    loading,
  } = useEdmDocumentationNameQuery({
    fetchPolicy: 'cache-and-network',
    skip: !id,
    variables: { id },
  });
  const data = (queryData || previousData)?.data;
  const firstLoading = loading && queryData === undefined;

  const [createVersionMutation, { loading: mutationLoading }] =
    useEdmDocumentationVersionCreateMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // AI auto-fill of the notes field requires the AiAssistance module on the
  // company plan (Write) AND the current user/team isn't individually
  // restricted from it.
  const isAiEnabled =
    getUserAccessByCompany(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write &&
    getUserAccessByComponent(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write;
  const { credits: aiCredits } = useAiBudgetStatus(isAiEnabled);
  // Some plan regions can't index video even when AiAssistance is enabled, so
  // a video attachment must still hide the AI notes-generation button.
  const isVideoAiIndexingSupported = useAiIndexableMediaKinds(
    isAiEnabled,
  ).includes(AiMediaKindEnum.Video);

  const formFields = useMemo(
    () =>
      getVersionFormFields({
        isFullAdmin,
        isPlanLocked,
        t,
        isAiEnabled,
        isVideoAiIndexingSupported,
        aiCredits,
      }),
    [
      isFullAdmin,
      isPlanLocked,
      t,
      isAiEnabled,
      isVideoAiIndexingSupported,
      aiCredits,
    ],
  );

  const defaultValues = useMemo(
    () => ({
      [VersionFormFieldNames.DocumentName]: data?.name || '',
      [VersionFormFieldNames.Attachment]: null,
      [VersionFormFieldNames.Notes]: '',
      [VersionFormFieldNames.RequireReread]: true,
    }),
    [data],
  );

  const onSubmit = useCallback(
    (values: Values) => {
      const attachment = values[VersionFormFieldNames.Attachment];
      if (!attachment) return;
      // `requiredToReread` is gated by `documentation_management` on the BE,
      // omit it for LimitedRead users to avoid UPGRADE_PLAN_REQUIRED.
      const attributes: DocumentationVersionCreateInputObject = {
        attachmentAttached: _omit(attachment as Record<string, unknown>, 'url'),
        notes: (values[VersionFormFieldNames.Notes] as string) || undefined,
        ...(isPlanLocked
          ? {}
          : {
              requiredToReread: Boolean(
                values[VersionFormFieldNames.RequireReread],
              ),
            }),
      };
      createVersionMutation({
        variables: {
          documentationId: id,
          attributes,
        },
        onCompleted: () => {
          toast({ content: t('update-document-success') });
          navigate(
            APP_URLS.dashboard.documentation.view.getDynamicPath({
              pathParts: { id },
            }),
          );
        },
      });
    },
    [createVersionMutation, id, isPlanLocked, navigate, t],
  );

  const onCancel = useCallback(() => {
    navigate(
      APP_URLS.dashboard.documentation.view.getDynamicPath({
        pathParts: { id },
      }),
    );
  }, [id, navigate]);

  const breadCrumbsData = useMemo(
    () =>
      getBreadCrumbsDataByKey({
        pathname,
        t,
        entity: data as Values | undefined,
      }),
    [pathname, t, data],
  );

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      {firstLoading ? (
        <CircleLoader />
      ) : (
        <DashboardContentMaxWidth>
          <PageHeader title={t('update-document')} />
          <PageBody>
            <WhiteBox>
              <Form
                actionsComponent={DocumentVersionFormActions}
                actionsComponentProps={{ onCancel }}
                defaultValues={defaultValues}
                fields={formFields}
                loading={mutationLoading}
                onSubmit={onSubmit}
              />
            </WhiteBox>
          </PageBody>
        </DashboardContentMaxWidth>
      )}
    </>
  );
}
